import React from "react";
import {
  Box,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  IconButton,
  Tooltip,
  Card,
  CardContent,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";

// ─── Theme tokens (shared — import from a central theme file if you have one) ──
export const T = {
  surface: "#ffffff",
  border: "#e5e7eb",
  borderLight: "#f0f1f5",
  green: "#16a34a",
  greenBg: "#f0fdf4",
  greenBorder: "#bbf7d0",
  red: "#dc2626",
  redBg: "#fef2f2",
  redBorder: "#fecaca",
  blue: "#2563eb",
  blueBg: "#eff6ff",
  blueBorder: "#bfdbfe",
  amber: "#d97706",
  purple: "#7c3aed",
  purpleBg: "#f5f3ff",
  purpleBorder: "#ddd6fe",
  text: "#111827",
  textSecondary: "#374151",
  muted: "#6b7280",
  subtle: "#9ca3af",
  headerBg: "#f9fafb",
};

const card = {
  background: T.surface,
  border: `1px solid ${T.border}`,
  borderRadius: 2,
  boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmtDuration = (s) => {
  const n = parseInt(s, 10);
  if (!n || isNaN(n)) return "—";
  return n < 60 ? `${n}s` : `${Math.floor(n / 60)}m ${n % 60}s`;
};

const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

// ─── Column definitions ───────────────────────────────────────────────────────
// Pass `columns` prop to override which columns are shown and in what order.
// Each column: { key, label, render? }
// If `render` is omitted, falls back to default cell renderers below.
const DEFAULT_COLUMNS = [
  "mobile",
  "campaign_name",
  "answer_status",
  "dtmf",
  "billing_duration",
  "agent_number",
  "hangup_cause",
  "createdOn",
  "recording_file",
];

const COLUMN_LABELS = {
  mobile: "Mobile",
  campaign_name: "Campaign",
  answer_status: "Status",
  dtmf: "DTMF",
  billing_duration: "Duration",
  agent_number: "Agent",
  hangup_cause: "Hangup Cause",
  createdOn: "Date & Time",
  recording_file: "",
};

// ─── Default cell renderers ───────────────────────────────────────────────────
const defaultCellRenderers = {
  mobile: (row) => (
    <TableCell
      key="mobile"
      sx={{
        fontFamily: "monospace !important",
        fontWeight: "600 !important",
        color: `${T.text} !important`,
      }}
    >
      {row.mobile}
    </TableCell>
  ),

  campaign_name: (row) => (
    <TableCell key="campaign_name">
      <Tooltip title={row.campaign_name} placement="top">
        <Typography
          noWrap
          sx={{ maxWidth: 160, fontSize: 13, color: T.textSecondary }}
        >
          {row.campaign_name}
        </Typography>
      </Tooltip>
    </TableCell>
  ),

  answer_status: (row) => (
    <TableCell key="answer_status">
      <Chip
        label={row.answer_status}
        size="small"
        sx={{
          fontSize: 11,
          fontWeight: 700,
          height: 22,
          background: row.answer_status === "Answered" ? T.greenBg : T.redBg,
          color: row.answer_status === "Answered" ? T.green : T.red,
          border: `1px solid ${
            row.answer_status === "Answered" ? T.greenBorder : T.redBorder
          }`,
        }}
      />
    </TableCell>
  ),

  dtmf: (row) => (
    <TableCell key="dtmf">
      {row.dtmf ? (
        <Box
          sx={{
            display: "inline-flex",
            width: 24,
            height: 24,
            borderRadius: "50%",
            background: T.purpleBg,
            border: `1px solid ${T.purpleBorder}`,
            alignItems: "center",
            justifyContent: "center",
            color: T.purple,
            fontSize: 12,
            fontWeight: 700,
          }}
        >
          {row.dtmf}
        </Box>
      ) : (
        <span style={{ color: T.subtle }}>—</span>
      )}
    </TableCell>
  ),

  billing_duration: (row) => (
    <TableCell
      key="billing_duration"
      sx={{
        fontFamily: "monospace !important",
        fontWeight: "600 !important",
        color: `${T.amber} !important`,
      }}
    >
      {fmtDuration(row.billing_duration)}
    </TableCell>
  ),

  agent_number: (row) => (
    <TableCell
      key="agent_number"
      sx={{ fontFamily: "monospace !important", fontSize: "12px !important" }}
    >
      {row.agent_number || <span style={{ color: T.subtle }}>—</span>}
    </TableCell>
  ),

  hangup_cause: (row) => (
    <TableCell key="hangup_cause">
      <Tooltip title={row.hangup_cause} placement="top">
        <Typography noWrap sx={{ maxWidth: 150, fontSize: 12, color: T.muted }}>
          {row.hangup_cause || "—"}
        </Typography>
      </Tooltip>
    </TableCell>
  ),

  createdOn: (row) => (
    <TableCell
      key="createdOn"
      sx={{
        fontSize: "12px !important",
        color: `${T.muted} !important`,
        whiteSpace: "nowrap",
      }}
    >
      {fmtDate(row.createdOn)}
    </TableCell>
  ),

  recording_file: (row) => (
    <TableCell key="recording_file">
      {row.recording_file ? (
        <Tooltip title="Play recording">
          <IconButton
            size="small"
            onClick={() => window.open(row.recording_file, "_blank")}
            sx={{
              color: T.blue,
              p: 0.5,
              "&:hover": { background: T.blueBg },
            }}
          >
            <PlayArrowIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      ) : (
        <span style={{ color: T.subtle }}>—</span>
      )}
    </TableCell>
  ),
};

// ─── VoiceCallLogsTable ───────────────────────────────────────────────────────
/**
 * @param {object[]}  rows          - Array of call log records
 * @param {boolean}   loading       - Show loading state
 * @param {number}    page          - Current page (1-based)
 * @param {number}    totalPages    - Total page count
 * @param {number}    [totalItems]  - Total record count (shown in header)
 * @param {Function}  onPageChange  - Called with new page number: (page: number) => void
 * @param {string[]}  [columns]     - Ordered list of column keys to display (defaults to all)
 * @param {object}    [cellRenderers] - Map of columnKey → (row) => <TableCell>
 *                                     Override any default renderer or add custom columns
 * @param {string}    [title]       - Card header title (default: "Call Logs")
 * @param {boolean}   [hideCard]    - Render the table without the Card wrapper
 */
const VoiceCallLogsTable = ({
  rows = [],
  loading = false,
  page = 1,
  totalPages = 1,
  totalItems,
  onPageChange,
  columns = DEFAULT_COLUMNS,
  cellRenderers = {},
  title = "Call Logs",
  hideCard = false,
}) => {
  const renderers = { ...defaultCellRenderers, ...cellRenderers };
  const colCount = columns.length;

  const tableContent = (
    <>
      {/* ── Header ── */}
      <Box
        sx={{
          px: 2.5,
          py: 1.8,
          borderBottom: `1px solid ${T.border}`,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography sx={{ fontWeight: 700, color: T.text, fontSize: 15 }}>
          {title}
          {totalItems > 0 && (
            <Typography
              component="span"
              sx={{ fontSize: 12, color: T.muted, ml: 1, fontWeight: 400 }}
            >
              {totalItems} records
            </Typography>
          )}
        </Typography>
      </Box>

      {/* ── Table ── */}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ background: T.headerBg }}>
              {columns.map((key) => (
                <TableCell
                  key={key}
                  sx={{
                    color: T.muted,
                    fontSize: 11,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: 0.6,
                    borderBottom: `1px solid ${T.border}`,
                    whiteSpace: "nowrap",
                    py: 1.2,
                    px: 1.5,
                  }}
                >
                  {COLUMN_LABELS[key] ?? key}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={colCount}
                  align="center"
                  sx={{ py: 6, color: T.muted, borderBottom: "none" }}
                >
                  Loading…
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={colCount}
                  align="center"
                  sx={{ py: 6, color: T.muted, borderBottom: "none" }}
                >
                  No records match your filters
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow
                  key={row._id}
                  sx={{
                    "&:hover": { background: "#fafafa" },
                    "& td": {
                      borderBottom: `1px solid ${T.borderLight}`,
                      fontSize: 13,
                      py: "8px",
                      px: 1.5,
                      color: T.textSecondary,
                    },
                  }}
                >
                  {columns.map((key) =>
                    renderers[key]
                      ? renderers[key](row)
                      : // Generic fallback for unknown / custom columns
                        React.createElement(
                          TableCell,
                          { key },
                          row[key] ?? <span style={{ color: T.subtle }}>—</span>
                        )
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ── Pagination ── */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 2.5,
          py: 1.5,
          borderTop: `1px solid ${T.border}`,
        }}
      >
        <Typography sx={{ fontSize: 12, color: T.muted }}>
          Page {page} of {totalPages || 1}
        </Typography>
        <Pagination
          count={totalPages || 1}
          page={page}
          size="small"
          onChange={(_, v) => onPageChange?.(v)}
          sx={{
            "& .MuiPaginationItem-root": {
              color: T.muted,
              border: `1px solid ${T.border}`,
              "&.Mui-selected": {
                background: T.blue,
                color: "#fff",
                border: "none",
                fontWeight: 700,
              },
              "&:hover": { background: T.blueBg },
            },
          }}
        />
      </Box>
    </>
  );

  if (hideCard) {
    return <Box sx={{ ...card, overflow: "hidden" }}>{tableContent}</Box>;
  }

  return (
    <Card sx={card}>
      <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
        {tableContent}
      </CardContent>
    </Card>
  );
};

export default VoiceCallLogsTable;
export { DEFAULT_COLUMNS, COLUMN_LABELS, defaultCellRenderers };
